import React, { useRef } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { NavLink } from 'react-router-dom';
import { Box, Button, FormLabel, Typography, useTheme } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
  COLOR_DENIM,
  COLOR_DISABLED_BG,
  COLOR_DISABLED_TEXT,
  COLOR_HUMMINGBIRD,
  COLOR_RED,
  COLOR_REGENT_ST_BLUE,
  COLOR_WHITE,
  FONT_WEIGHT_SEMI_BOLD,
} from 'popTheme';
import { PopInputField } from './PopInputField';
import { ScrollableContainer } from './ScrollableContainer';

export const UserDetails = ({ nextStep, handleChange, values }) => {
  const intl = useIntl();
  const theme = useTheme();

  const messages = defineMessages({
    registerLabelTitle: {
      id: 'register.label.title',
      defaultMessage: "Let's get you setup!",
      description: 'Title text for register step 1 section',
    },
    registerLabelStep: {
      id: 'register.label.step1of2',
      defaultMessage: 'Step 1 of 2',
      description: 'Step indicator text for register step 1 section',
    },
    registerLabelEmail: {
      id: 'register.label.email',
      defaultMessage: 'Email Address',
      description: 'Label for the email input',
    },
    registerEmailError: {
      id: 'register.email.error',
      defaultMessage: 'Incorrect email format.',
      description: 'Error text for invalid email input',
    },
    registerLabelPassword: {
      id: 'register.label.password',
      defaultMessage: 'Password',
      description: 'Label for the password input',
    },
    registerPasswordError: {
      id: 'register.password.error1',
      defaultMessage: 'Password does not meet requirements.',
      description: 'Error text for invalid password part 1',
    },
    registerPasswordInstructions: {
      id: 'register.password.instructions',
      defaultMessage:
        'Password must be at least 8 characters, including 1 capitalized letter, 1 small letter, 1 special character and 1 numeric character.',
      description: 'Instruction text for password input',
    },
    registerLabelConfirmPassword: {
      id: 'register.label.confirm.password',
      defaultMessage: 'Confirm Password',
      description: 'Label for the confirm password input',
    },
    registerConfirmPasswordError: {
      id: 'register.confirm.password.error',
      defaultMessage: 'Passwords do not match.',
      description: 'Error text for unmatched passwords',
    },
    registerLabelButtonNext: {
      id: 'register.label.button.next',
      defaultMessage: 'Next',
      description: 'Label for the next button',
    },
    registerLabelPrivatePolicy: {
      id: 'register.label.private.policy',
      defaultMessage:
        'Your personal information belongs to you. Read more about our privacy policy ',
      description: 'Link text for private policy',
    },
    registerLabelPrivatePolicyLink: {
      id: 'register.label.private.policy.link',
      defaultMessage: 'here',
      description: 'Link text for private policy',
    },
  });

  const isDisabled =
    !(values.email.length > 0 && values.isValidEmail) ||
    !(values.password.length > 0 && values.isValidPassword) ||
    !(values.confirmPassword.length > 0 && values.isValidPasswordAgain);

  const handleNext = (e) => {
    e.preventDefault();
    nextStep();
  };

  let emailInput = useRef(null);
  let passwordInput = useRef(null);
  let confirmPasswordInput = useRef(null);

  return (
    <ScrollableContainer>
      <NavLink data-testid="register-step1-back-link" to="/signin">
        <ArrowBackIcon
          sx={{
            color: COLOR_REGENT_ST_BLUE,
            position: 'relative',
            fontSize: '1.25rem',
            top: '1.25rem',
            left: { xs: '1.25rem', sm: '0rem' },
          }}
        />
      </NavLink>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        sx={{
          [theme.breakpoints.only('xs')]: {
            gap: '0.3125rem',
            px: '3rem',
            py: '4.5rem',
          },
          [theme.breakpoints.up('sm')]: {
            gap: '0.9375rem',
            px: '3.75rem',
            py: '9.0625rem',
          },
        }}
      >
        <Typography
          variant="h2"
          sx={{
            color: COLOR_WHITE,
            textAlign: 'center',
            [theme.breakpoints.up('xs')]: {
              fontSize: '1.5625rem',
            },
            [theme.breakpoints.up('sm')]: {
              fontSize: '2rem',
            },
            [theme.breakpoints.up('lg')]: {
              fontSize: '2.4375rem',
            },
          }}
        >
          {intl.formatMessage(messages.registerLabelTitle)}
        </Typography>
        <Typography
          sx={{
            color: COLOR_WHITE,
            [theme.breakpoints.up('xs')]: {
              fontSize: '0.8125rem',
            },
            [theme.breakpoints.up('sm')]: {
              fontSize: '1rem',
            },
            [theme.breakpoints.up('lg')]: {
              fontSize: '1.25rem',
            },
          }}
        >
          {intl.formatMessage(messages.registerLabelStep)}
        </Typography>
        <Box
          component="form"
          data-testid="register-step1-form"
          noValidate
          alignItems="center"
          sx={{
            [theme.breakpoints.up('xs')]: {
              minWidth: '16rem',
            },
            [theme.breakpoints.up('sm')]: {
              minWidth: '26.25rem',
            },
          }}
        >
          <Box py={{ xs: '0.9375rem', sm: '0.625rem' }}>
            <FormLabel
              required
              htmlFor={'register-step1-email-input'}
              sx={{
                color: COLOR_WHITE,
                fontWeight: FONT_WEIGHT_SEMI_BOLD,
                '.MuiFormLabel-asterisk': {
                  color: COLOR_RED,
                },
                [theme.breakpoints.up('xs')]: {
                  px: '0.625rem',
                  py: '0.5rem',
                  fontSize: '0.625rem',
                },
                [theme.breakpoints.up('sm')]: {
                  px: '0.9375rem',
                  py: '0.625rem',
                  fontSize: '0.8125rem',
                },
              }}
            >
              {intl.formatMessage(messages.registerLabelEmail)}
            </FormLabel>
            <PopInputField
              error={!values.isValidEmail}
              onChange={handleChange('email')}
              value={values.email}
              inputRef={emailInput}
              required
              fullWidth
              id="email"
              name="email"
              autoComplete="email"
              autoFocus
              size="small"
              data-testid="register-step1-email-input"
              onClickClearButton={() => {
                handleChange('email', emailInput)(null);
              }}
              iconDataTestId="register-step1-email-clear"
            />
            <Box>
              {!values.isValidEmail && (
                <Typography variant="subtitle1" align="center" color={COLOR_WHITE} py="0.9375rem">
                  {intl.formatMessage(messages.registerEmailError)}
                </Typography>
              )}
            </Box>
          </Box>
          <Box py={{ xs: '0.9375rem', sm: '0.625rem' }}>
            <FormLabel
              required
              htmlFor={'register-step1-password-input'}
              sx={{
                color: COLOR_WHITE,
                fontWeight: FONT_WEIGHT_SEMI_BOLD,
                '.MuiFormLabel-asterisk': {
                  color: COLOR_RED,
                },
                [theme.breakpoints.up('xs')]: {
                  px: '0.625rem',
                  py: '0.5rem',
                  fontSize: '0.625rem',
                },
                [theme.breakpoints.up('sm')]: {
                  px: '0.9375rem',
                  py: '0.625rem',
                  fontSize: '0.8125rem',
                },
              }}
            >
              {intl.formatMessage(messages.registerLabelPassword)}
            </FormLabel>
            <PopInputField
              error={!values.isValidPassword}
              onChange={handleChange('password')}
              value={values.password}
              inputRef={passwordInput}
              required
              fullWidth
              name="password"
              type="password"
              id="password"
              size="small"
              data-testid="register-step1-password-input"
              onClickClearButton={() => {
                handleChange('password', passwordInput)(null);
              }}
              iconDataTestId="register-step1-password-clear"
            />
            <Box>
              {!values.isValidPassword && (
                <Typography variant="subtitle1" align="center" color={COLOR_WHITE} py="0.9375rem">
                  {intl.formatMessage(messages.registerPasswordError)}
                </Typography>
              )}
            </Box>
          </Box>
          <Typography
            variant="subtitle1"
            align="center"
            color={COLOR_WHITE}
            fontSize={{ xs: '0.625rem', sm: '0.8125rem' }}
          >
            {intl.formatMessage(messages.registerPasswordInstructions)}
          </Typography>
          <Box py={{ xs: '0.9375rem', sm: '0.625rem' }}>
            <FormLabel
              required
              htmlFor="register-step1-confirm-password-input"
              sx={{
                color: COLOR_WHITE,
                fontWeight: FONT_WEIGHT_SEMI_BOLD,
                '.MuiFormLabel-asterisk': {
                  color: COLOR_RED,
                },
                [theme.breakpoints.up('xs')]: {
                  px: '0.625rem',
                  py: '0.5rem',
                  fontSize: '0.625rem',
                },
                [theme.breakpoints.up('sm')]: {
                  px: '0.9375rem',
                  py: '0.625rem',
                  fontSize: '0.8125rem',
                },
              }}
            >
              {intl.formatMessage(messages.registerLabelConfirmPassword)}
            </FormLabel>
            <PopInputField
              error={!values.isValidPasswordAgain}
              onChange={handleChange('confirmPassword')}
              value={values.confirmPassword}
              inputRef={confirmPasswordInput}
              required
              fullWidth
              name="confirmPassword"
              type="password"
              id="confirmPassword"
              size="small"
              data-testid="register-step1-confirm-password-input"
              onClickClearButton={() => {
                handleChange('confirmPassword', confirmPasswordInput)(null);
              }}
              iconDataTestId="register-step1-confirm-password-clear"
            />
            <Box>
              {!values.isValidPasswordAgain && (
                <Typography variant="subtitle1" align="center" color={COLOR_WHITE} py="0.9375rem">
                  {intl.formatMessage(messages.registerConfirmPasswordError)}
                </Typography>
              )}
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Button
              type="submit"
              variant="contained"
              disabled={isDisabled}
              onClick={handleNext}
              data-testid="register-step1-next-button"
              sx={{
                my: '1.25rem',
                bgcolor: COLOR_HUMMINGBIRD,
                color: COLOR_DENIM,
                minWidth: '13.125rem',
                fontWeight: 600,
                px: '0.625rem',
                ':hover': {
                  opacity: 0.5,
                },
                '&.Mui-disabled': {
                  background: COLOR_DISABLED_BG,
                  color: COLOR_DISABLED_TEXT,
                },
                [theme.breakpoints.up('xs')]: {
                  py: '0.5rem',
                  fontSize: '0.8125rem',
                },
                [theme.breakpoints.up('sm')]: {
                  py: '0.625rem',
                  fontSize: '1rem',
                },
              }}
            >
              {intl.formatMessage(messages.registerLabelButtonNext)}
            </Button>
            <Typography
              variant="body1"
              textAlign="center"
              sx={{
                color: COLOR_WHITE,
                [theme.breakpoints.up('xs')]: {
                  fontSize: '0.625rem',
                  width: '13.5rem',
                },
                [theme.breakpoints.up('sm')]: {
                  fontSize: '0.8125rem',
                  width: '17.5rem',
                },
              }}
            >
              {intl.formatMessage(messages.registerLabelPrivatePolicy)}
              <NavLink to="https://www.popyouth.ca/terms-conditions" style={{ color: COLOR_WHITE }}>
                {intl.formatMessage(messages.registerLabelPrivatePolicyLink)}
              </NavLink>
              .
            </Typography>
          </Box>
        </Box>
      </Box>
    </ScrollableContainer>
  );
};
